import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, Loader2, Sparkles, TrendingDown, Check, RefreshCw,
} from "lucide-react";
import { CONTACT } from "@/lib/data";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

export default function SavingsCalculator({ variant = "page" }) {
  const { t } = useI18n();
  const [phase, setPhase] = useState("upload"); // upload | analyzing | result | done
  const [preview, setPreview] = useState("");
  const [result, setResult] = useState(null);
  const [current, setCurrent] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", service_address: "" });
  const [leadId, setLeadId] = useState(null);
  const [svc, setSvc] = useState({ container_size: "", frequency: "", quantity: "" });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const pct = result?.savings_percent ?? 12;
  const curNum = parseFloat(current) || 0;
  const projected = curNum ? +(curNum * (1 - pct / 100)).toFixed(2) : 0;
  const monthlySave = curNum ? +(curNum - projected).toFixed(2) : 0;
  const annualSave = +(monthlySave * 12).toFixed(2);

  const analyze = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("calc.errImg")); return; }
    setPreview(URL.createObjectURL(file));
    setPhase("analyzing");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/calculator/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setCurrent(data.current_amount ? String(data.current_amount) : "");
      setSvc({
        container_size: data.container_size || "",
        frequency: data.frequency || "",
        quantity: data.quantity ? String(data.quantity) : "",
      });
      setLeadId(data.lead_id || null);
      setForm((f) => ({
        name: data.customer_name || f.name,
        business: data.business_name || f.business,
        email: data.customer_email || f.email,
        phone: data.customer_phone || f.phone,
        service_address: data.service_address || f.service_address,
      }));
      setPhase("result");
      if (!data.current_amount) toast.info(t("calc.infoAmount"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("calc.errRead"));
      setResult({ savings_percent: 20, invoice_path: null });
      setPhase("result");
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error(t("calc.errContact")); return; }
    if (!curNum) { toast.error(t("calc.errAmount")); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/calculator/lead`, {
        ...form, current_amount: curNum, invoice_path: result?.invoice_path || null,
        container_size: svc.container_size || "", frequency: svc.frequency || "",
        quantity: svc.quantity ? parseInt(svc.quantity, 10) : null,
        lead_id: leadId || null,
      });
      setPhase("done");
    } catch {
      toast.error(t("calc.errGeneric") + CONTACT.phone);
    } finally { setSubmitting(false); }
  };

  const reset = () => { setPhase("upload"); setResult(null); setCurrent(""); setPreview(""); setLeadId(null); setForm({ name: "", email: "", phone: "", business: "", service_address: "" }); setSvc({ container_size: "", frequency: "", quantity: "" }); };

  const dark = variant === "home";
  const inputCls = `w-full px-4 py-3 rounded-xl border text-sm focus:outline-none transition-colors ${dark ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-emerald-400" : "bg-white border-[#E7E5DF] text-[#0B0F14] placeholder:text-slate-400 focus:border-emerald-600/60"}`;

  return (
    <div className={`rounded-3xl p-6 sm:p-8 ${dark ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] shadow-soft"}`} data-testid="savings-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} className="text-emerald-400" />
        <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-emerald-500">WiseWaste™ Savings Calculator</span>
      </div>
      <h3 className={`font-display font-extrabold text-2xl sm:text-3xl ${dark ? "text-white" : "text-[#0B0F14]"}`}>{t("calc.title")}</h3>
      <p className={`mt-2 text-sm ${dark ? "text-white/70" : "text-slate-600"}`}>{t("calc.sub")}</p>

      {/* UPLOAD */}
      {phase === "upload" && (
        <div className="mt-6">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="calc-file-input"
            onChange={(e) => analyze(e.target.files?.[0])} />
          <button onClick={() => fileRef.current?.click()} data-testid="calc-upload-btn"
            className={`w-full flex flex-col items-center justify-center gap-3 py-12 rounded-2xl border-2 border-dashed transition-colors ${dark ? "border-white/25 hover:border-emerald-400 hover:bg-white/5" : "border-[#E7E5DF] hover:border-emerald-600/50 hover:bg-emerald-50/40"}`}>
            <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-500 text-[#0A2C2C]"><Camera size={26} /></span>
            <span className={`font-semibold ${dark ? "text-white" : "text-[#0B0F14]"}`}>{t("calc.upload")}</span>
            <span className={`text-xs flex items-center gap-1.5 ${dark ? "text-white/60" : "text-slate-500"}`}><Upload size={13} /> JPG, PNG or WEBP</span>
          </button>
        </div>
      )}

      {/* ANALYZING */}
      {phase === "analyzing" && (
        <div className="mt-6 flex flex-col items-center gap-4 py-12" data-testid="calc-analyzing">
          {preview && <img loading="lazy" decoding="async" src={preview} alt="Your invoice" className="max-h-40 rounded-xl border border-white/10 opacity-70" />}
          <div className="flex items-center gap-3"><Loader2 size={22} className="animate-spin text-emerald-500" /><span className={dark ? "text-white/80" : "text-slate-600"}>{t("calc.reading")}</span></div>
        </div>
      )}

      {/* RESULT */}
      {phase === "result" && (
        <div className="mt-6 space-y-6" data-testid="calc-result">
          <div className={`grid sm:grid-cols-2 gap-4`}>
            <div>
              <label className={`block text-xs font-semibold mb-1.5 ${dark ? "text-white/70" : "text-slate-500"}`}>{t("calc.currentLabel")} {result?.confidence && result?.current_amount ? `(AI: ${result.confidence} confidence)` : ""}</label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${dark ? "text-white/60" : "text-slate-400"}`}>$</span>
                <input type="number" step="0.01" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="calc-current-amount"
                  className={`${inputCls} pl-8`} placeholder="0.00" />
              </div>
              {result?.vendor && <p className={`text-xs mt-1 ${dark ? "text-white/50" : "text-slate-400"}`}>{t("calc.detected")} {result.vendor}</p>}
            </div>
            <div className={`rounded-xl p-4 ${dark ? "bg-emerald-500/15" : "bg-emerald-50"} flex flex-col justify-center`}>
              <span className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold"><TrendingDown size={14} /> {t("calc.ewsPrice")} ({pct}% {t("calc.less")})</span>
              <span className={`font-display font-extrabold text-2xl ${dark ? "text-white" : "text-[#0B0F14]"}`}>{money(projected)}<span className="text-sm font-normal opacity-60">/mo</span></span>
            </div>
          </div>

          {/* AI-detected service details (editable) */}
          <div data-testid="calc-service-details">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles size={13} className="text-emerald-500" />
              <span className={`text-xs font-semibold ${dark ? "text-white/70" : "text-slate-500"}`}>{t("calc.svcDetails")} {(svc.container_size || svc.frequency || svc.quantity) ? t("calc.svcDetailsAi") : t("calc.svcDetailsAdd")}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input className={inputCls} placeholder={t("calc.phSize")} value={svc.container_size} onChange={(e) => setSvc({ ...svc, container_size: e.target.value })} data-testid="calc-container-size" />
              <input className={inputCls} placeholder={t("calc.phFreq")} value={svc.frequency} onChange={(e) => setSvc({ ...svc, frequency: e.target.value })} data-testid="calc-frequency" />
              <input className={inputCls} type="number" min="1" step="1" placeholder={t("calc.phQty")} value={svc.quantity} onChange={(e) => setSvc({ ...svc, quantity: e.target.value })} data-testid="calc-quantity" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`rounded-2xl p-5 text-center ${dark ? "bg-white/5" : "bg-[#F0EFEA]"}`} data-testid="calc-monthly-savings">
              <p className={`text-xs ${dark ? "text-white/60" : "text-slate-500"}`}>{t("calc.saveMonth")}</p>
              <p className="font-display font-extrabold text-2xl text-emerald-600">{money(monthlySave)}</p>
            </div>
            <div className={`rounded-2xl p-5 text-center ${dark ? "bg-emerald-500 text-[#0A2C2C]" : "bg-[#0E3B3B] text-white"}`} data-testid="calc-annual-savings">
              <p className="text-xs opacity-80">{t("calc.saveYear")}</p>
              <p className="font-display font-extrabold text-2xl">{money(annualSave)}</p>
            </div>
          </div>

          <div className={`rounded-2xl p-4 flex gap-3 ${dark ? "bg-white/5 border border-white/10" : "bg-white border border-emerald-100"}`} data-testid="calc-ai-note">
            <Sparkles size={18} className="text-emerald-500 shrink-0 mt-0.5" />
            <p className={`text-xs leading-relaxed ${dark ? "text-white/75" : "text-slate-600"}`}>
              {t("calc.aiNote")}
            </p>
          </div>

          <form onSubmit={submitLead} className="space-y-3" data-testid="calc-lead-form">
            <p className={`text-sm font-semibold ${dark ? "text-white" : "text-[#0B0F14]"}`}>{t("calc.reachYou")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder={t("calc.phName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="calc-name" />
              <input className={inputCls} placeholder={t("calc.phBusiness")} value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} data-testid="calc-business" />
              <input className={inputCls} type="email" placeholder={t("calc.phEmail")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="calc-email" />
              <input className={inputCls} placeholder={t("calc.phPhone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="calc-phone" />
            </div>
            <input className={inputCls} placeholder={t("calc.phAddress")} value={form.service_address} onChange={(e) => setForm({ ...form, service_address: e.target.value })} data-testid="calc-address" />
            <div className="flex flex-wrap gap-3 pt-1">
              <button type="submit" disabled={submitting} data-testid="calc-submit-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 text-[#0A2C2C] font-bold hover:bg-emerald-400 active:scale-95 transition-all disabled:opacity-60">
                {submitting ? <><Loader2 size={17} className="animate-spin" /> {t("calc.sending")}</> : <>{t("calc.sendBtn")}</>}
              </button>
              <button type="button" onClick={reset} data-testid="calc-reset-btn"
                className={`inline-flex items-center gap-2 px-5 py-3.5 rounded-full text-sm font-semibold transition-colors ${dark ? "text-white/70 hover:text-white" : "text-slate-500 hover:text-[#0B0F14]"}`}>
                <RefreshCw size={15} /> {t("calc.startOver")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* DONE */}
      {phase === "done" && (
        <div className="mt-6 text-center py-10" data-testid="calc-done">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-500 text-[#0A2C2C] mx-auto mb-5"><Check size={32} /></span>
          <h4 className={`font-display font-extrabold text-2xl ${dark ? "text-white" : "text-[#0B0F14]"}`}>{t("calc.doneTitle").replace("{amt}", money(annualSave))}</h4>
          <p className={`mt-2 text-sm max-w-md mx-auto ${dark ? "text-white/70" : "text-slate-600"}`}>{t("calc.doneBody")}{CONTACT.phone}.</p>
          <button onClick={reset} className={`mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full border text-sm font-semibold transition-colors ${dark ? "border-white/25 text-white hover:bg-white/10" : "border-[#E7E5DF] text-[#0B0F14] hover:border-emerald-600/50"}`} data-testid="calc-again-btn">
            <RefreshCw size={15} /> {t("calc.again")}
          </button>
        </div>
      )}
    </div>
  );
}
